import { Controller } from 'stimulus'
import formatCurrency from 'helpers/new-format-currency'

/**
 * Manages js for the gift funds step (StimulusJS controller)
 *
 * @class BorrowerGiftFundsController
 * @memberof Controllers
 */
export default class extends Controller {
  static targets = [
    'accountAmount',
    'accountAmountError',
    'giftDeposited',
    'giftDepositedError',
    'accountOwner',
    'hiddenOwner',
    'accountOwnerError',
    'accountSource',
    'accountSourceError',
    'giftFundClearOrCancelButton',
    'giftFundCreateOrUpdateButton',
    'giftFundTotal',
    'giftFundsList',
    'control',
    'noGiftFundError',
    'templateSource'
  ]

  /**
   * @function Controllers.BorrowerGiftFundsController.createOrUpdate
   * @memberof Controllers
   * @description Creates or updates an giftFund, or throws errors on giftFund on
   * client-side click event.
   */
  createOrUpdate() {
    const { inputLegit, ...fieldsLegit } = this._inputLegit()
    const HTTPVerb = this.giftFundCreateOrUpdateButtonTarget.dataset.method

    if (inputLegit) {
      this._postOrUpdate(HTTPVerb)
    } else {
      this._removeErrorState()
      this._displayFieldErrors(fieldsLegit)
    }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController.editGiftFund
   * @memberof Controllers
   * @description Removes an giftFund from the list and places its values in the
   * form for update.
   *
   * @param {Event} event - User initiated click event.
   */
  editGiftFund(event) {
    const giftFundElement   = event.target.parentElement.parentElement.parentElement.parentElement
    const controlElement = giftFundElement.querySelector('.crud-item-list__list-item-controls')

    const amount = controlElement.dataset.amount
    const id     = controlElement.dataset.id
    const deposited = giftFundElement.querySelector('.crud-item-list__list-item-name').innerText
    const source = giftFundElement.querySelector('.crud-item-list__list-item-account').innerText
    const owner  = giftFundElement.querySelector('.crud-item-list__list-item-owner').innerText

    this.accountSourceTarget.value = source
    this.giftDepositedTarget.value = deposited
    this.accountAmountTarget.value = amount
    this.accountAmountTarget.imask.updateValue()
    this.accountAmountTarget.imask.updateControl()

    if (!this.hasHiddenOwnerTarget) {
      this.accountOwnerTarget.value = owner
    }

    this.cachedGiftFund = {
      amount,
      id,
      deposited,
      owner,
      source
    }

    giftFundElement.remove()

    this._calculateTotal()
    this._changeButtonsToEdit(id)
  }

  /**
   * @function Controllers.BorrowerGiftFundsController.clearOrCancel
   * @memberof Controllers
   * @description Upon UI button click, either clears the form fields or kicks
   * off an update call to the back-end.
   */
  clearOrCancel() {
    const method = this.giftFundClearOrCancelButtonTarget.dataset.method

    if (method === 'CLEAR') {
      this._blankValues()
      this._removeErrorState()
    } else {
      this._addGiftFundToUI(this.cachedGiftFund)
      this.cachedGiftFund = null
      this._calculateTotal()
      this._blankValues()
      this._removeErrorState()
    }

    this._changeButtonsToCreate()
  }

  /**
   * @function Controllers.BorrowerGiftFundsController.destroyGiftFund
   * @memberof Controllers
   * @description Destroys an giftFund on click from the user.
   * @param {Event} event - User initiated click event.
   */
  destroyGiftFund(event) {
    event.preventDefault()

    const giftFundId = event.target.parentElement.parentElement.dataset.id

    fetch(`/v1/borrower_gift_funds/${giftFundId}`, {
      method:      'DELETE',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors'
    })
      .then(response => this._removeGiftFundFromUI(event.target))
      .catch(error => {
      // console.error('Error: ', error)
      })
  }

  // Private

  /**
   * @function Controllers.BorrowerGiftFundsController._postOrUpdate
   * @memberof Controllers
   * @description Posts or updates to the backend
   * @private
   *
   * @param {String} HTTPVerb - Verb to use for call to back-end
   */
  _postOrUpdate(HTTPVerb) {
    if (HTTPVerb == 'POST') {
      this._createGiftFund()
    } else {
      const id = this.giftFundCreateOrUpdateButtonTarget.dataset.id

      this._updateGiftFund(id)
      this._changeButtonsToCreate()
    }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._changeButtonsToEdit
   * @memberof Controllers
   * @description Manipulates the text of the form buttons to match the edit
   * state, 'cancel' and 'save', and alters dataset state to be used by other
   * methods.
   * @private
   *
   * @param {String} id - giftFund id
   */
  _changeButtonsToEdit(id) {
    this.giftFundClearOrCancelButtonTarget.innerText      = 'CANCEL'
    this.giftFundClearOrCancelButtonTarget.dataset.method = 'CANCEL'

    this.giftFundCreateOrUpdateButtonTarget.innerText      = 'SAVE'
    this.giftFundCreateOrUpdateButtonTarget.dataset.method = 'PATCH'
    this.giftFundCreateOrUpdateButtonTarget.dataset.id     = id
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._changeButtonsToCreate
   * @memberof Controllers
   * @description Manipulates the text of the form buttons to match the edit
   * state, 'clear' and 'add giftFund', and alters dataset state to be used by
   * other methods.
   * @private
   */
  _changeButtonsToCreate() {
    this.giftFundClearOrCancelButtonTarget.innerText      = 'CLEAR'
    this.giftFundClearOrCancelButtonTarget.dataset.method = 'CLEAR'

    this.giftFundCreateOrUpdateButtonTarget.innerText      = 'ADD GIFT'
    this.giftFundCreateOrUpdateButtonTarget.dataset.method = 'POST'
    this.giftFundCreateOrUpdateButtonTarget.dataset.id     = ''
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._displayFieldErrors
   * @memberof Controllers
   * @description Takes an object with booleans for each required field and
   * displays errors for those that aren't legit.
   * @private
   *
   * @param {Object} fieldsLegit
   * @param {Boolean} fieldsLegit.amountLegit - true if amount input legit, false otherwise
   * @param {Boolean} fieldsLegit.OwnerLegit - true if owner input legit, false otherwise
   * @param {Boolean} fieldsLegit.SourceLegit - true if source input legit, false otherwise
   * @param {Boolean} fieldsLegit.giftDepositedLegit - true if source input legit, false otherwise
   */
  _displayFieldErrors(fieldsLegit) {
    if (!fieldsLegit.amountLegit) {
      this.accountAmountTarget.classList.add('form__text-field--error')
      this.accountAmountErrorTarget.classList.remove('form__errors-list--hidden')
    }

    if (!fieldsLegit.ownerLegit) {
      this.accountOwnerTarget.classList.add('form__select--error')
      this.accountOwnerErrorTarget.classList.remove('form__errors-list--hidden')
    }

    if (!fieldsLegit.sourceLegit) {
      this.accountSourceTarget.classList.add('form__select--error')
      this.accountSourceErrorTarget.classList.remove('form__errors-list--hidden')
    }

    if (!fieldsLegit.giftDepositedLegit) {
      this.giftDepositedTarget.classList.add('form__text-field--error')
      this.giftDepositedErrorTarget.classList.remove('form__errors-list--hidden')
    }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._inputLegit
   * @memberof Controllers
   * @description Checks the required fields to see if they have a non-empty
   *
   * @private
   *
   * @return {Object} fieldsLegit
   * @return {Boolean} fieldsLegit.amountLegit - true if present, false otherwise
   * @return {Boolean} fieldsLegit.OwnerLegit - true if present, false otherwise
   * @return {Boolean} fieldsLegit.SourceLegit - true if present, false otherwise
   * @return {Boolean} fieldsLegit.giftDepositedLegit - true if present, false otherwise
   */
  _inputLegit() {
    const amountLegit      = this.accountAmountTarget.value !== '' && this.accountAmountTarget.value !== '0'
    const ownerLegit       = this.accountOwnerTarget.value !== ''
    const sourceLegit      = this.accountSourceTarget.value !== ''
    const giftDepositedLegit = this.giftDepositedTarget.value !== ''

    const inputLegit = amountLegit && ownerLegit && sourceLegit && giftDepositedLegit

    return {
      inputLegit,
      amountLegit,
      ownerLegit,
      sourceLegit,
      giftDepositedLegit
    }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._removeErrorState
   * @memberof Controllers
   * @description Removes all the errors from the UI.
   * @private
   */
  _removeErrorState() {
    this.noGiftFundErrorTarget.innerText = ''
    this.accountAmountTarget.classList.remove('form__text-field--error')
    this.accountAmountErrorTarget.classList.add('form__errors-list--hidden')

    this.giftDepositedTarget.classList.remove('form__text-field--error')
    this.giftDepositedErrorTarget.classList.add('form__errors-list--hidden')

    this.accountSourceTarget.classList.remove('form__select--error')
    this.accountSourceErrorTarget.classList.add('form__errors-list--hidden')

    if (!this.hasHiddenOwnerTarget) {
      this.accountOwnerTarget.classList.remove('form__select--error')
      this.accountOwnerErrorTarget.classList.add('form__errors-list--hidden')
    }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._blankValues
   * @memberof Controllers
   * @description Removes all the values from the form fields.
   * @private
   */
  _blankValues() {
    this.accountAmountTarget.value = ''
    this.accountAmountTarget.imask.updateValue()
    this.giftDepositedTarget.value   = ''
    this.accountSourceTarget.value = ''

    if (!this.hasHiddenOwnerTarget) {
      this.accountOwnerTarget.value  = ''
    }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._getFormValues
   * @memberof Controllers
   * @description Gets values from the form for other parties
   * @private
   *
   * @return {Object} form_values
   * @return {String} form_values.amount GiftFund amount
   * @return {String} form_values.deposited GiftFund deposited
   * @return {String} form_values.owner GiftFund owner
   * @return {String} form_values.source GiftFund source
   */
  _getFormValues() {
    const amount = this._removeCommas(this.accountAmountTarget.value)
    const deposited = this.giftDepositedTarget.value.includes('Not') ? false : true
    const owner  = this.accountOwnerTarget.value
    const source = this.accountSourceTarget.value

    return { amount, deposited, owner, source }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._prepareGiftFundFromForm
   * @memberof Controllers
   * @description Gets form values from _getFormValues and adds an id to it
   * @private
   *
   * @param  {String} id
   * @return {Object} form_values
   * @return {String} form_values.amount GiftFund amount
   * @return {String} form_values.deposited GiftFund deposited
   * @return {String} form_values.owner GiftFund owner
   * @return {String} form_values.source GiftFund source
   * @return {String} form_values.id GiftFund id
   */
  _prepareGiftFundFromForm(id) {
    const { amount, deposited, owner, source } = this._getFormValues()

    return { amount, id, deposited, owner, source }
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._successResolution
   * @memberof Controllers
   * @description Called on the successful resolution of a POST or UPDATE call
   * to the back-end, clears form, removes errors, adds the giftFund to the giftFund
   * list, and calculates the giftFund total
   * @private
   *
   * @param {Object} giftFund
   * @param {Integer} giftFund.id
   * @param {String} giftFund.deposited
   * @param {String} giftFund.owner
   * @param {String} giftFund.amount
   * @param {String} giftFund.source
   */
  _successResolution(giftFund) {
    this._blankValues()
    this._removeErrorState()
    this._addGiftFundToUI(giftFund)
    this._calculateTotal()
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._calculateTotal
   * @memberof Controllers
   * @description Loops through the control targets pulling the amount,
   * accumulating it
   * @private
   */
  _calculateTotal() {
    const total = this.controlTargets.reduce((acc, target) => {
      const maybeAmount = target.dataset.amount

      if (maybeAmount) {
        return acc + parseInt(maybeAmount)
      }

      return acc
    }, 0)

    this.giftFundTotalTarget.textContent = formatCurrency(total)
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._removeGiftFundFromUI
   * @memberof Controllers
   * @description Removes an giftFund from the ui and recalculates total
   *
   * @param {Object} element - GiftFund element to remove
   * @private
   */
  _removeGiftFundFromUI(element) {
    element.parentElement.parentElement.parentElement.remove()

    this._calculateTotal()
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._addGiftFundToUI
   * @memberof Controllers
   * @description Adds an giftFund to the ui using the template.
   * @private
   *
   * @param {Object} giftFund
   * @param {Integer} giftFund.id
   * @param {String} giftFund.deposited
   * @param {String} giftFund.owner
   * @param {String} giftFund.amount
   * @param {String} giftFund.source
   */
  _addGiftFundToUI(giftFund) {
    const clone = this.templateSourceTarget.cloneNode(true)

    clone.removeAttribute('style')
    clone.removeAttribute('data-target')
    clone.querySelector('.crud-item-list__list-item-account').textContent = giftFund.source
    clone.querySelector('.crud-item-list__list-item-name').textContent = giftFund.deposited === true ? 'Deposited' : 'Not deposited'

    if (!this.hasHiddenOwnerTarget) {
      clone.querySelector('.crud-item-list__list-item-owner').textContent = giftFund.owner
    }

    clone.querySelector('.crud-item-list__list-item-total').textContent = formatCurrency(giftFund.amount)
    clone.querySelector('.crud-item-list__list-item-controls').dataset.id = giftFund.id
    clone.querySelector('.crud-item-list__list-item-controls').dataset.amount = giftFund.amount

    const firstChild = this.giftFundsListTarget.firstElementChild

    this.giftFundsListTarget.insertBefore(clone, firstChild)
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._removeCommas
   * @memberof Controllers
   * @description Removes the commas from the input masked number
   * @private
   *
   * @param {String} value - value with commas
   *
   * @return {String} Same value without commas
   *
   * @example
   *
   * input  = '300,000'
   * output = '300000'
   */
  _removeCommas(value) {
    return value.replace(/,/g, '')
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._updateGiftFund
   * @memberof Controllers
   * @description Retrieves form values, combines it with the id and sends it
   * off to the back-end for persistence
   * @private
   *
   * @param {String} id - giftFund id
   */
  _updateGiftFund(id) {
    const giftFund = this._getFormValues()
    const data  = { gift_fund: giftFund, id }

    fetch(`/v1/borrower_gift_funds/${id}`, {
      method:      'PATCH',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })
      .then(response => response.json())
      .then(response => this._successResolution(response))
      .catch(error => {
        // console.error('Error: ', error)
      })
  }

  /**
   * @function Controllers.BorrowerGiftFundsController._createGiftFund
   * @memberof Controllers
   * @description Creates a giftFund through a call to the back-end
   * off to the back-end for persistence
   * @private
   */
  _createGiftFund() {
    const slug        = this.data.get('slug')
    const application = this.data.get('application')
    const giftFund       = this._getFormValues()

    const data = { gift_fund: giftFund, application, slug }

    fetch('/v1/borrower_gift_funds', {
      method:      'POST',
      credentials: 'same-origin',
      headers:     { 'content-type': 'application/json' },
      mode:        'cors',
      body:        JSON.stringify(data)
    })
      .then(response => response.json())
      .then(response => this._successResolution(response))
      .catch(error => {
      // console.error('Error: ', error)
      })
  }
}
